import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Select, Table, Tag, Typography, Space, Statistic, Progress } from 'antd';
import { BarChartOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { API_BASE, authHeaders } from '../config/api';
import './admin.css';

const { Title, Text } = Typography;

const ChannelComparison = () => {
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [channelData, setChannelData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('7d');

  const loadWebsites = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/my/websites`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const data = await res.json();
      const websiteList = Array.isArray(data?.items) ? data.items : Array.isArray(data?.websites) ? data.websites : [];
      setWebsites(websiteList);
      if (websiteList.length > 0) {
        setSelectedWebsite(websiteList[0].id);
      }
    } catch (err) {
      console.error('Error loading websites:', err);
    }
  }, []);

  const loadChannelData = useCallback(async () => {
    if (!selectedWebsite) return;
    
    setLoading(true);
    try {
      // Fetch analytics data grouped by channel/source
      const res = await fetch(
        `${API_BASE}/my/analytics/events?website_id=${selectedWebsite}&days=${timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 1}&limit=1000`,
        { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
      );
      const data = await res.json();
      const events = Array.isArray(data?.items) ? data.items : [];

      // Group events by channel (referrer source)
      const channelMap = new Map();
      
      events.forEach(event => {
        const referrer = event.referrer || event.source || 'Direct';
        const channel = categorizeChannel(referrer);
        
        if (!channelMap.has(channel)) {
          channelMap.set(channel, {
            channel,
            pageviews: 0,
            clicks: 0,
            uniqueUsers: new Set(),
            sessions: new Set()
          });
        }
        
        const channelStats = channelMap.get(channel);
        if (event.type === 'pageview') channelStats.pageviews++;
        if (event.type === 'click') channelStats.clicks++;
        
        // Extract visitor_id from data JSON
        const visitorId = event.data?.visitor_id;
        if (visitorId) channelStats.uniqueUsers.add(visitorId);
        
        // Extract session_id from data JSON
        const sessionId = event.data?.session_id;
        if (sessionId) channelStats.sessions.add(sessionId);
      });

      // Convert to array and calculate metrics
      const channelArray = Array.from(channelMap.values()).map(channel => ({
        channel: channel.channel,
        pageviews: channel.pageviews,
        clicks: channel.clicks,
        users: channel.uniqueUsers.size,
        sessions: channel.sessions.size,
        bounceRate: 0, // Simplified - would need session analysis
        ctr: channel.pageviews > 0 ? 
          Math.round((channel.clicks / channel.pageviews) * 100) : 0
      }));

      // Sort by pageviews descending
      channelArray.sort((a, b) => b.pageviews - a.pageviews);
      
      setChannelData(channelArray);
    } catch (err) {
      console.error('Error loading channel data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedWebsite, timeRange]);

  const categorizeChannel = (referrer) => {
    if (!referrer || referrer === 'Direct') return 'Direct';
    
    const ref = referrer.toLowerCase();
    
    // Social Media
    if (ref.includes('facebook') || ref.includes('fb.com')) return 'Facebook';
    if (ref.includes('twitter') || ref.includes('t.co')) return 'Twitter';
    if (ref.includes('linkedin')) return 'LinkedIn';
    if (ref.includes('instagram')) return 'Instagram';
    if (ref.includes('youtube')) return 'YouTube';
    if (ref.includes('tiktok')) return 'TikTok';
    if (ref.includes('pinterest')) return 'Pinterest';
    
    // Search Engines
    if (ref.includes('google')) return 'Google Search';
    if (ref.includes('bing')) return 'Bing';
    if (ref.includes('yahoo')) return 'Yahoo';
    if (ref.includes('duckduckgo')) return 'DuckDuckGo';
    
    // Email
    if (ref.includes('mail') || ref.includes('email')) return 'Email';
    
    // Referral
    return 'Referral';
  };

  const getChannelColor = (channel) => {
    const colors = {
      'Direct': '#64748b',
      'Google Search': '#4285f4',
      'Facebook': '#1877f2',
      'Twitter': '#1da1f2',
      'LinkedIn': '#0a66c2',
      'Instagram': '#e4405f',
      'YouTube': '#ff0000',
      'Email': '#ea580c',
      'Referral': '#8b5cf6'
    };
    return colors[channel] || '#94a3b8';
  };

  useEffect(() => {
    loadWebsites();
  }, [loadWebsites]);

  useEffect(() => {
    loadChannelData();
  }, [loadChannelData]);

  const columns = [
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      render: (channel) => (
        <Space>
          <div style={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            backgroundColor: getChannelColor(channel) 
          }} />
          <Text strong>{channel}</Text>
        </Space>
      )
    },
    {
      title: 'Pageviews',
      dataIndex: 'pageviews',
      key: 'pageviews',
      sorter: (a, b) => a.pageviews - b.pageviews,
      render: (value) => <Text>{value.toLocaleString()}</Text>
    },
    {
      title: 'Clicks',
      dataIndex: 'clicks',
      key: 'clicks',
      sorter: (a, b) => a.clicks - b.clicks,
      render: (value) => <Text>{value.toLocaleString()}</Text>
    },
    {
      title: 'Users',
      dataIndex: 'users',
      key: 'users',
      sorter: (a, b) => a.users - b.users,
      render: (value) => <Text>{value.toLocaleString()}</Text>
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions',
      key: 'sessions',
      sorter: (a, b) => a.sessions - b.sessions,
      render: (value) => <Text>{value.toLocaleString()}</Text>
    },
    {
      title: 'CTR',
      dataIndex: 'ctr',
      key: 'ctr',
      sorter: (a, b) => a.ctr - b.ctr,
      render: (value) => (
        <Space>
          <Progress 
            percent={Math.min(value, 100)} 
            size="small" 
            style={{ width: 60 }}
            strokeColor={value > 30 ? '#10b981' : value > 10 ? '#f59e0b' : '#ef4444'}
          />
          <Text>{value}%</Text>
        </Space>
      )
    },
    {
      title: 'Performance',
      key: 'performance',
      render: (_, record) => {
        const avgConversion = channelData.reduce((sum, c) => sum + c.ctr, 0) / channelData.length;
        const isAboveAvg = record.ctr > avgConversion;
        return (
          <Tag color={isAboveAvg ? 'success' : 'default'} icon={isAboveAvg ? <ArrowUpOutlined /> : <ArrowDownOutlined />}>
            {isAboveAvg ? 'Above Avg' : 'Below Avg'}
          </Tag>
        );
      }
    }
  ];

  const totalPageviews = channelData.reduce((sum, c) => sum + c.pageviews, 0);
  const totalClicks = channelData.reduce((sum, c) => sum + c.clicks, 0);
  const totalUsers = channelData.reduce((sum, c) => sum + c.users, 0);
  const avgCtr = channelData.length > 0 ? 
    Math.round(channelData.reduce((sum, c) => sum + c.ctr, 0) / channelData.length) : 0;

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <BarChartOutlined style={{ marginRight: 12, color: '#6366f1' }} />
        Channel Comparison
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Compare traffic sources and marketing channels to identify your best performers
      </Text>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="Total Pageviews" 
              value={totalPageviews} 
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="Total Clicks" 
              value={totalClicks} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="Unique Users" 
              value={totalUsers} 
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="Avg CTR" 
              value={avgCtr} 
              suffix="%" 
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle">
          <div>
            <Text strong style={{ marginRight: 8 }}>Website:</Text>
            <Select
              style={{ width: 250 }}
              value={selectedWebsite}
              onChange={setSelectedWebsite}
              options={websites.map(w => ({
                label: w.url.replace(/^https?:\/\//, ''),
                value: w.id
              }))}
            />
          </div>
          <div>
            <Text strong style={{ marginRight: 8 }}>Time Range:</Text>
            <Select
              style={{ width: 150 }}
              value={timeRange}
              onChange={setTimeRange}
              options={[
                { label: 'Last 24 Hours', value: '1d' },
                { label: 'Last 7 Days', value: '7d' },
                { label: 'Last 30 Days', value: '30d' }
              ]}
            />
          </div>
        </Space>
      </Card>

      {/* Channel Comparison Table */}
      <Card title="Channel Performance Breakdown" style={{ borderRadius: 14 }}>
        <Table
          columns={columns}
          dataSource={channelData}
          rowKey="channel"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default ChannelComparison;
